import { useReducer, useCallback, useEffect, useRef } from 'react';
import type { DocumentState, IMasterDocumentAdapter, DeleteLineResult } from '../adapter/MasterDocumentAdapter.types';
import type { MasterDocumentTemplate, TotalsAggregation } from '../template/MasterDocumentTemplate.types';
import type { DocumentLineData, DocumentHeaderData, FieldErrorMap } from '../MasterDocument.types';
import { evaluateExpression } from '../template/expressionEvaluator';
import { isConflictError, type ConflictDetails } from './conflictError';
import { toast } from '../../Toast';
import { useMasterDocumentTranslation } from '../i18n';
import { classifyError } from './userFacingError';

// ════════════════════════════════════════════════════════════
// LINE TOTALS ENGINE — propaga agregados de líneas → headerValues
// ════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════
// RESERVED ERROR KEYS — _conflict / _forbidden
// ════════════════════════════════════════════════════════════

const CONFLICT_KEY = '_conflict';
const FORBIDDEN_KEY = '_forbidden';

// ════════════════════════════════════════════════════════════
// RESERVED VERSION KEYS — retención del token de concurrencia optimista
// ════════════════════════════════════════════════════════════

/** Clave por defecto del rowVersion de cada línea (vive en `DocumentLineData`). */
const DEFAULT_LINE_VERSION_KEY = '_row_version';
/** Clave por defecto del rowVersion de la cabecera (vive en `headerValues`). */
const DEFAULT_HEADER_VERSION_KEY = '_rowVersion';

/**
 * Mergea el token de versión de la línea (`versionKey`) leído del ESTADO del
 * documento en el `data` que se enviará al adapter, para que el round-trip de
 * concurrencia optimista no dependa de que el modal recolecte los campos
 * internos `_`-prefijados.
 *
 * Reglas:
 * - Línea nueva (`lineId === null`) → sin versión previa: no inyecta nada.
 * - El formulario tiene prioridad: si `data` ya trae la clave, se respeta.
 * - Si la línea del estado no tiene versión, se devuelve `data` intacto.
 */
function mergeLineVersion(
  data: Record<string, unknown>,
  lineId: string | null,
  lines: DocumentLineData[],
  versionKey: string,
): Record<string, unknown> {
  if (!lineId) return data;
  if (data[versionKey] != null) return data;
  const current = lines.find((l) => l.id === lineId);
  const version = current?.[versionKey];
  if (version == null) return data;
  return { ...data, [versionKey]: version };
}

/**
 * Garantiza que la versión de cabecera (`versionKey`) viaje en `headerValues`.
 * `headerValues` ya transporta su propio `_rowVersion`; cuando no lo trae pero el
 * estado tiene `serverVersion`, se inyecta como respaldo. El valor existente en
 * `headerValues` siempre tiene prioridad.
 */
function mergeHeaderVersion(
  header: DocumentHeaderData,
  serverVersion: string | undefined,
  versionKey: string,
): DocumentHeaderData {
  if (header[versionKey] != null) return header;
  if (serverVersion == null) return header;
  return { ...header, [versionKey]: serverVersion };
}

/**
 * Inyecta el token de versión de la CABECERA (`headerVersionKey`) en el payload
 * de una mutación de LÍNEA, para que el backend valide la concurrencia del header
 * al guardar/agregar una línea (no solo al editar la cabecera). Aplica a líneas
 * nuevas y existentes.
 *
 * El valor se toma de `headerValues[headerVersionKey]` y, como respaldo, de
 * `serverVersion`. Degrada con gracia: si no hay versión de cabecera o el payload
 * ya la trae, se devuelve intacto (compatibilidad con backends que aún no la
 * validan en línea).
 */
function withHeaderVersion(
  data: Record<string, unknown>,
  headerValues: DocumentHeaderData,
  serverVersion: string | undefined,
  headerVersionKey: string,
): Record<string, unknown> {
  if (data[headerVersionKey] != null) return data;
  const version = headerValues[headerVersionKey] ?? serverVersion;
  if (version == null) return data;
  return { ...data, [headerVersionKey]: version };
}

interface PartitionedErrors {
  conflict: { message?: string } | null;
  forbidden: { message: string } | null;
  /** Errores de campo restantes (sin las claves reservadas). */
  fieldErrors: FieldErrorMap;
}

/**
 * Separa las claves reservadas `_conflict` y `_forbidden` del resto de errores de
 * campo. Permite que un `SaveResult` 409/403 dispare el flujo de conflicto / banner
 * en lugar de renderizarse como un error de campo genérico.
 */
export function partitionReservedErrors(errors: FieldErrorMap): PartitionedErrors {
  const { [CONFLICT_KEY]: conflictErr, [FORBIDDEN_KEY]: forbiddenErr, ...fieldErrors } = errors;
  return {
    conflict: conflictErr ? { message: conflictErr.message } : null,
    forbidden: forbiddenErr ? { message: forbiddenErr.message } : null,
    fieldErrors,
  };
}

function applyLineTotals(
  rules: Record<string, TotalsAggregation>,
  lines: DocumentLineData[],
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [key, agg] of Object.entries(rules)) {
    switch (agg.op) {
      case 'sum':
        result[key] = lines.reduce((acc, l) => acc + (Number(l[agg.field]) || 0), 0);
        break;
      case 'sumProduct':
        result[key] = lines.reduce((acc, l) => {
          const product = agg.fields.reduce((p, f) => p * (Number(l[f]) || 0), 1);
          return acc + product;
        }, 0);
        break;
      case 'sumDiscount':
        result[key] = lines.reduce((acc, l) => {
          const qty = Number(l[agg.qtyField]) || 0;
          const price = Number(l[agg.priceField]) || 0;
          const discount = Number(l[agg.discountField]) || 0;
          return acc + qty * price * (1 - discount / 100);
        }, 0);
        break;
      case 'diff':
        result[key] = (result[agg.a] ?? 0) - (result[agg.b] ?? 0);
        break;
      case 'add':
        result[key] = agg.fields.reduce((acc, f) => acc + (result[f] ?? 0), 0);
        break;
      case 'ref':
        result[key] = (result[agg.field] ?? 0) * (agg.multiplier ?? 1);
        break;
    }
  }
  return result;
}

// ════════════════════════════════════════════════════════════
// ACTIONS
// ════════════════════════════════════════════════════════════

type Action =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: DocumentState }
  | { type: 'LOAD_ERROR'; payload: string }
  | { type: 'SET_HEADER_FIELD'; key: string; value: unknown }
  | { type: 'SET_HEADER_ERRORS'; errors: FieldErrorMap }
  | { type: 'SET_LINES'; lines: DocumentLineData[] }
  | { type: 'ADD_LINE'; line: DocumentLineData }
  | { type: 'UPDATE_LINE'; lineId: string; data: Record<string, unknown> }
  | { type: 'DELETE_LINE'; lineId: string }
  | { type: 'DUPLICATE_LINE'; lineId: string; newLine: DocumentLineData }
  | { type: 'SET_DOC_STATE'; docState: string }
  | { type: 'SET_SAVING'; saving: boolean }
  | { type: 'SET_ERROR'; error?: string }
  | { type: 'SET_FACTBOX_DATA'; sectionKey: string; data: Record<string, unknown> }
  | { type: 'SET_SERVER_VERSION'; version: string }
  | { type: 'SET_CONFLICT'; conflict: NonNullable<DocumentState['conflict']> }
  | { type: 'CLEAR_CONFLICT' }
  | { type: 'SET_FORBIDDEN'; forbidden: NonNullable<DocumentState['forbidden']> }
  | { type: 'CLEAR_FORBIDDEN' }
  | { type: 'MERGE_STATE'; payload: Partial<DocumentState> };

// ════════════════════════════════════════════════════════════
// INITIAL STATE
// ════════════════════════════════════════════════════════════

function createInitialState(template: MasterDocumentTemplate): DocumentState {
  return {
    documentId: undefined,
    mode: 'create',
    docState: template.stateMachine.initial,
    headerValues: {},
    lines: [],
    headerErrors: {},
    lineErrors: {},
    loading: false,
    saving: false,
    error: undefined,
    serverVersion: undefined,
    factBoxData: {},
    conflict: null,
    forbidden: null,
  };
}

// ════════════════════════════════════════════════════════════
// REDUCER
// ════════════════════════════════════════════════════════════

function reducer(state: DocumentState, action: Action): DocumentState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, loading: true, error: undefined };
    case 'LOAD_SUCCESS':
      return { ...action.payload, loading: false, saving: false };
    case 'LOAD_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'SET_HEADER_FIELD':
      return {
        ...state,
        headerValues: { ...state.headerValues, [action.key]: action.value },
      };
    case 'SET_HEADER_ERRORS':
      return { ...state, headerErrors: action.errors };
    case 'SET_LINES':
      return { ...state, lines: action.lines };
    case 'ADD_LINE':
      return { ...state, lines: [...state.lines, action.line] };
    case 'UPDATE_LINE':
      return {
        ...state,
        lines: state.lines.map((l) =>
          l.id === action.lineId ? { ...l, ...action.data } : l,
        ),
      };
    case 'DELETE_LINE':
      return {
        ...state,
        lines: state.lines.filter((l) => l.id !== action.lineId),
      };
    case 'DUPLICATE_LINE': {
      const idx = state.lines.findIndex((l) => l.id === action.lineId);
      if (idx === -1) return state;
      const next = [...state.lines];
      next.splice(idx + 1, 0, action.newLine);
      return { ...state, lines: next };
    }
    case 'SET_DOC_STATE':
      return { ...state, docState: action.docState };
    case 'SET_SAVING':
      return { ...state, saving: action.saving };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_FACTBOX_DATA':
      return {
        ...state,
        factBoxData: { ...state.factBoxData, [action.sectionKey]: action.data },
      };
    case 'SET_SERVER_VERSION':
      return { ...state, serverVersion: action.version };
    case 'SET_CONFLICT':
      return { ...state, conflict: action.conflict };
    case 'CLEAR_CONFLICT':
      return { ...state, conflict: null };
    case 'SET_FORBIDDEN':
      return { ...state, forbidden: action.forbidden };
    case 'CLEAR_FORBIDDEN':
      return { ...state, forbidden: null };
    case 'MERGE_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════
// HOOK
// ════════════════════════════════════════════════════════════

/**
 * Trabajo que debe ocurrir DESPUÉS de que el backend confirmó una escritura y
 * devolvió su id, dentro de la misma interacción del usuario.
 *
 * Existe para los datos que no viajan en el payload del documento y necesitan un
 * id que solo existe una vez persistido — hoy, los valores de las entidades
 * dinámicas (EAV). Es deliberadamente un *callback* y no una llamada directa: el
 * reducer no sabe nada de entidades dinámicas ni debe saberlo, y quien conoce los
 * formularios montados es la capa que renderiza.
 *
 * Contrato: **no deben lanzar y no deben fallar la operación**. Para cuando se
 * invocan, el documento o la línea ya están guardados; revertir no es una opción y
 * propagar el error solo lograría que el usuario crea que perdió todo.
 */
export interface MasterDocumentPersistenceHooks {
  /** El encabezado quedó persistido con este id (recién creado o existente). */
  onHeaderPersisted?: (documentId: string) => Promise<void> | void;
  /** La línea quedó persistida con este id (recién creada o existente). */
  onLinePersisted?: (lineId: string) => Promise<void> | void;
}

export function useMasterDocumentReducer(
  template: MasterDocumentTemplate,
  adapter: IMasterDocumentAdapter,
  documentId?: string,
  initialState?: DocumentState,
  onSaved?: () => void,
  viewOnly?: boolean,
  onNavigateToList?: () => void,
  persistenceHooks?: MasterDocumentPersistenceHooks,
) {
  const { t } = useMasterDocumentTranslation();

  // Los hooks de post-persistencia se leen por REF y no por dependencia: el host
  // los reconstruye en cada render, y meterlos en las dependencias de
  // `handleSaveHeader`/`handleLineSave` invalidaría esos callbacks continuamente
  // sin ninguna ganancia — se invocan siempre desde un evento del usuario, cuando
  // el ref ya está al día.
  const persistenceHooksRef = useRef(persistenceHooks);
  persistenceHooksRef.current = persistenceHooks;

  // Merge con defaults para garantizar que lines/headerErrors/lineErrors nunca sean undefined
  const [state, dispatch] = useReducer(
    reducer,
    initialState
      ? { ...createInitialState(template), ...initialState }
      : createInitialState(template),
  );

  // Claves reservadas de versión (concurrencia optimista). Configurables vía
  // template.versionKeys; por defecto `_row_version` (línea) / `_rowVersion` (cabecera).
  const lineVersionKey = template.versionKeys?.line ?? DEFAULT_LINE_VERSION_KEY;
  const headerVersionKey = template.versionKeys?.header ?? DEFAULT_HEADER_VERSION_KEY;

  // ── Conflicto DURO (409 / OPTIMISTIC_LOCK_VIOLATION) ──
  // Fuente de verdad única: el 409 del servidor en cualquier mutación. Descarta
  // la edición en vuelo y muestra el modal NO descartable (la única acción es
  // recargar). `expected`/`actual` se registran para diagnóstico.
  const raiseConflict = useCallback((details: ConflictDetails) => {
    if (details.expected != null || details.actual != null) {
      console.warn(
        '[MasterDocument] Conflicto de concurrencia optimista (row_version)',
        { expected: details.expected, actual: details.actual },
      );
    }
    dispatch({
      type: 'SET_CONFLICT',
      conflict: {
        hard: true,
        message: details.message,
        expected: details.expected,
        actual: details.actual,
      },
    });
  }, []);

  /**
   * Manejo unificado del error lanzado por una mutación: si es un 409 de
   * concurrencia, dispara el conflicto duro y devuelve `true`; en caso contrario
   * registra el error genérico y devuelve `false`.
   */
  const handleMutationError = useCallback(
    (err: unknown): boolean => {
      const conflict = isConflictError(err);
      if (conflict) {
        raiseConflict(conflict);
        return true;
      }
      // El texto tecnico de JS no llega al usuario: `state.error` alimenta un
      // toast, asi que se guarda el mensaje clasificado y el crudo va al log.
      const { message, technical } = classifyError(err, t);
      if (technical) console.error('[MasterDocument]', technical);
      dispatch({ type: 'SET_ERROR', error: message });
      return false;
    },
    [raiseConflict, t],
  );

  // ── Sync lineTotals → headerValues cuando cambian las líneas ──
  const linesRef = useRef(state.lines);
  useEffect(() => {
    const rules = template.lineTotals;
    if (!rules || Object.keys(rules).length === 0) return;
    // Solo disparar si realmente cambiaron las líneas (misma ref = no cambió)
    if (state.lines === linesRef.current) return;
    linesRef.current = state.lines;
    const totals = applyLineTotals(rules, state.lines);
    for (const [key, value] of Object.entries(totals)) {
      dispatch({ type: 'SET_HEADER_FIELD', key, value });
    }
  }); // sin deps: corre en cada render, pero la guarda de ref evita loops

  // ── Load document on mount ────────────────────────
  useEffect(() => {
    if (!documentId) return;

    let cancelled = false;
    dispatch({ type: 'LOAD_START' });

    adapter.getDocument(documentId).then(
      (doc) => {
        if (!cancelled) dispatch({
          type: 'LOAD_SUCCESS',
          payload: viewOnly ? { ...doc, mode: 'view' } : doc,
        });
      },
      (err) => {
        // `LOAD_ERROR` alimenta el MISMO `state.error` —y por tanto el mismo toast— que las
        // mutaciones, así que se clasifica igual que ellas. Era el último sitio que quedaba en
        // el camino crudo: el usuario veía texto saneado al guardar y el mensaje técnico de JS
        // ("Cannot read properties of undefined") al cargar, dentro del mismo componente.
        //
        // `t` se queda FUERA del array de dependencias a propósito: cambia con el idioma activo
        // y añadirlo volvería a pedir el documento cada vez que el usuario cambia de idioma. El
        // precio es que un mensaje de error ya emitido conserva el idioma en que se emitió.
        if (cancelled) return;
        const { message, technical } = classifyError(err, t);
        if (technical) console.error('[MasterDocument] carga del documento falló:', technical);
        dispatch({ type: 'LOAD_ERROR', payload: message });
      },
    );

    return () => { cancelled = true; };
  }, [documentId, adapter]);

  // ── Header field change with computed rules ───────
  const handleHeaderFieldChange = useCallback(
    (key: string, value: unknown) => {
      dispatch({ type: 'SET_HEADER_FIELD', key, value });

      // Evaluate computed rules that watch this field
      const newValues = { ...state.headerValues, [key]: value };
      for (const rule of template.computedRules) {
        if (rule.watch.includes(key)) {
          const computed = evaluateExpression(
            rule.expression,
            newValues,
            adapter.evaluateCustomRule?.bind(adapter),
          );
          dispatch({ type: 'SET_HEADER_FIELD', key: rule.targetField, value: computed });
          newValues[rule.targetField] = computed;
        }
      }
    },
    [state.headerValues, template.computedRules, adapter],
  );

  // ── Save header ───────────────────────────────────
  const handleSaveHeader = useCallback(async () => {
    dispatch({ type: 'SET_SAVING', saving: true });
    try {
      let docId = state.documentId;

      // Primer guardado: crear el registro en BD antes de guardar el header
      if (!docId && adapter.createDocument) {
        const { documentId: newId } = await adapter.createDocument(
          // La clase del ESTADO manda sobre la del template: una superficie que
          // sirve a varias clases la resuelve en el wizard, y el template solo
          // puede declarar una. Sin ella (todos los documentos de una sola clase)
          // se usa la del template, como siempre.
          state.documentClass ?? template.identity.documentClass,
          String(state.headerValues._companyId ?? ''),
          String(state.headerValues._coId ?? ''),
          String(state.headerValues._typeId ?? ''),
        );
        docId = newId;
        dispatch({ type: 'MERGE_STATE', payload: { documentId: newId, mode: 'edit' } });
      }

      if (!docId) return;

      const headerPayload = mergeHeaderVersion(state.headerValues, state.serverVersion, headerVersionKey);
      const result = await adapter.saveHeader(docId, headerPayload);
      if (result.errors) {
        const { conflict, forbidden, fieldErrors } = partitionReservedErrors(result.errors);
        if (conflict) raiseConflict(conflict);
        if (forbidden) dispatch({ type: 'SET_FORBIDDEN', forbidden });
        dispatch({ type: 'SET_HEADER_ERRORS', errors: fieldErrors });
      }
      if (result.serverVersion) {
        dispatch({ type: 'SET_SERVER_VERSION', version: result.serverVersion });
      }
      // Segunda fase: lo que cuelga del documento y necesitaba su id. Solo si el
      // backend aceptó el encabezado — colgarle valores adicionales a un header
      // rechazado dejaría datos huérfanos de una edición que el usuario todavía
      // tiene que corregir.
      if (!result.errors) {
        await persistenceHooksRef.current?.onHeaderPersisted?.(docId);
      }
    } catch (err) {
      handleMutationError(err);
    } finally {
      dispatch({ type: 'SET_SAVING', saving: false });
    }
  }, [state.documentId, state.documentClass, state.headerValues, state.serverVersion, headerVersionKey, adapter, template.identity.documentClass, raiseConflict, handleMutationError]);

  // ── Auto-save line totals → header ───────────────
  // Calcula los totales de las líneas proporcionadas y los persiste en el
  // header del backend inmediatamente, sin esperar a que el usuario guarde.
  // docIdOverride: usado cuando el documentId acaba de ser obtenido en el mismo
  // render cycle (ej: primera línea que crea el documento) y aún no está en state.
  const autoSaveLineTotals = useCallback(
    async (updatedLines: DocumentLineData[], docIdOverride?: string) => {
      const docId = docIdOverride ?? state.documentId;
      const rules = template.lineTotals;
      if (!rules || !docId || Object.keys(rules).length === 0) return;
      const totals = applyLineTotals(rules, updatedLines);
      // Incluir _autoSaveServerVersion para que el adapter pueda refrescar su versión
      const updatedHeaderValues = {
        ...state.headerValues,
        ...totals,
        _autoSaveServerVersion: state.serverVersion,
      };
      try {
        const result = await adapter.saveHeader(docId, updatedHeaderValues);
        if (result.errors) {
          const { conflict, forbidden } = partitionReservedErrors(result.errors);
          if (conflict) raiseConflict(conflict);
          if (forbidden) dispatch({ type: 'SET_FORBIDDEN', forbidden });
        }
        if (result.serverVersion) {
          dispatch({ type: 'SET_SERVER_VERSION', version: result.serverVersion });
        }
      } catch (err) {
        // Un 409 aquí (recálculo de totales en cabecera) es el mismo conflicto duro.
        const conflict = isConflictError(err);
        if (conflict) raiseConflict(conflict);
        else {
          dispatch({
            type: 'SET_ERROR',
            error: t('master_document.error.autoSaveTotals', {
              detail: classifyError(err, t).message,
            }),
          });
        }
      }
    },
    [template.lineTotals, state.documentId, state.headerValues, state.serverVersion, adapter, raiseConflict, t],
  );

  // ── Save line ─────────────────────────────────────
  const handleLineSave = useCallback(
    async (lineId: string | null, data: Record<string, unknown>): Promise<FieldErrorMap | undefined> => {
      // Separa las claves reservadas de un SaveLineResult.errors: `_conflict` (409)
      // dispara el conflicto duro y cierra el modal (return undefined); `_forbidden`
      // muestra el banner 403; el resto vuelve al modal como errores de campo.
      const partitionLineResult = (errors: FieldErrorMap): FieldErrorMap | undefined => {
        const { conflict, forbidden, fieldErrors } = partitionReservedErrors(errors);
        if (conflict) { raiseConflict(conflict); return undefined; }
        if (forbidden) dispatch({ type: 'SET_FORBIDDEN', forbidden });
        return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
      };
      dispatch({ type: 'SET_SAVING', saving: true });
      try {
        if (state.documentId) {
          // ── Documento ya guardado: persistir línea en backend ──
          // Reinyecta el _row_version de la línea (leído del estado) cuando se edita
          // una línea existente; las líneas nuevas (lineId === null) no llevan versión.
          // Además inyecta el row_version de la CABECERA en TODA mutación de línea
          // para que el backend valide la concurrencia del header al guardar la línea.
          const payload = withHeaderVersion(
            mergeLineVersion(data, lineId, state.lines, lineVersionKey),
            state.headerValues,
            state.serverVersion,
            headerVersionKey,
          );
          const result = await adapter.saveLine(state.documentId, lineId, payload);
          if (result.success) {
            // Merge: payload enviado (incl. _row_version) + lineData del backend (autoritativo,
            // refresca _row_version con la versión más reciente para la próxima edición).
            const merged = { ...payload, ...(result.lineData ?? {}) };
            if (lineId) {
              const updatedLines = state.lines.map((l) =>
                l.id === lineId ? { ...l, ...merged } : l,
              );
              dispatch({ type: 'UPDATE_LINE', lineId, data: merged });
              // Antes de los totales: el modal sigue montado mientras esto corre
              // —se cierra cuando `handleLineSave` resuelve—, y es de ahí de donde
              // salen los valores adicionales de la línea.
              await persistenceHooksRef.current?.onLinePersisted?.(lineId);
              await autoSaveLineTotals(updatedLines);
            } else {
              const newLine: DocumentLineData = {
                id: result.lineId,
                line_number: state.lines.length + 1,
                ...merged,
              } as DocumentLineData;
              dispatch({ type: 'ADD_LINE', line: newLine });
              if (result.lineId) {
                await persistenceHooksRef.current?.onLinePersisted?.(result.lineId);
              }
              await autoSaveLineTotals([...state.lines, newLine]);
            }
            // Sincronizar la versión/cabecera recalculadas por el backend tras el POST
            // /lines. Sin esto la versión local se queda atrás y el poll de conflicto
            // dispara un falso "alguien más editó". Se aplica DESPUÉS de UPDATE_LINE/
            // ADD_LINE; SET_SERVER_VERSION y MERGE_STATE(headerValues) no tocan `lines`.
            if (result.serverVersion) {
              dispatch({ type: 'SET_SERVER_VERSION', version: result.serverVersion });
            }
            if (result.headerValues) {
              dispatch({
                type: 'MERGE_STATE',
                payload: { headerValues: { ...state.headerValues, ...result.headerValues } },
              });
            }
          } else if (result.errors) {
            return partitionLineResult(result.errors);
          }
        } else if (lineId) {
          // ── Sin documento, editando línea local: actualizar en estado ──
          dispatch({ type: 'UPDATE_LINE', lineId, data });
        } else {
          // ── Sin documento, PRIMERA línea: intentar crear documento + línea juntos ──
          // El adapter recibe headerValues para poder crear el documento en el mismo request.
          // (En doc nuevo aún no hay row_version de cabecera, así que withHeaderVersion es no-op.)
          const result = await adapter.saveLine(
            null,
            null,
            withHeaderVersion(data, state.headerValues, state.serverVersion, headerVersionKey),
            state.headerValues,
          );
          if (result.success) {
            const merged = { ...data, ...(result.lineData ?? {}) };

            if (result.documentId) {
              // Backend creó el documento y la primera línea en una sola operación.
              // Actualizar el estado con el nuevo documentId, modo 'edit' y los campos
              // calculados por el backend (consecutivo, fecha, vencimiento, etc.).
              dispatch({
                type: 'MERGE_STATE',
                payload: {
                  documentId: result.documentId,
                  mode: 'edit' as const,
                  ...(result.serverVersion ? { serverVersion: result.serverVersion } : {}),
                  // Merge: primero los valores actuales del usuario, luego los del backend.
                  // Así se respetan los campos ingresados y se agregan los calculados.
                  ...(result.headerValues
                    ? { headerValues: { ...state.headerValues, ...result.headerValues } }
                    : {}),
                },
              });
              const newLine: DocumentLineData = {
                id: result.lineId,
                line_number: state.lines.length + 1,
                ...merged,
              } as DocumentLineData;
              dispatch({ type: 'ADD_LINE', line: newLine });
              // Documento y primera línea nacieron en la misma operación: los dos
              // ids existen recién ahora, así que las dos segundas fases se
              // disparan aquí. El encabezado va primero por orden de dependencia.
              await persistenceHooksRef.current?.onHeaderPersisted?.(result.documentId);
              if (result.lineId) {
                await persistenceHooksRef.current?.onLinePersisted?.(result.lineId);
              }
              // Pasar docIdOverride porque state.documentId aún no refleja el nuevo ID
              await autoSaveLineTotals([...state.lines, newLine], result.documentId);
            } else {
              // Adapter no soporta creación combinada (ej: mock básico sin documentId).
              // Agregar línea localmente con ID temporal; se persistirá al guardar cabecera.
              dispatch({
                type: 'ADD_LINE',
                line: {
                  id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  line_number: state.lines.length + 1,
                  ...merged,
                } as DocumentLineData,
              });
            }
          } else if (result.errors) {
            return partitionLineResult(result.errors);
          }
        }
      } catch (err) {
        handleMutationError(err);
      } finally {
        dispatch({ type: 'SET_SAVING', saving: false });
      }
      return undefined;
    },
    [state.documentId, state.lines, state.headerValues, state.serverVersion, lineVersionKey, headerVersionKey, adapter, autoSaveLineTotals, raiseConflict, handleMutationError],
  );

  // ── Delete line ───────────────────────────────────
  // LA CONFIRMACIÓN NO VIVE AQUÍ. Antes esta función abría un `window.confirm` nativo: un cuadro del
  // navegador, ajeno al estilo del kit, que además bloquea el hilo y obliga a parchear `window` para
  // poder probar el borrado. La confirmación se movió a la capa que renderiza
  // (`SmartMasterDocument`), que usa el `ConfirmDialog` del kit — el mismo patrón que `DocumentList`
  // ya había adoptado para las acciones de fila con `requiresConfirmation`.
  //
  // CONSECUENCIA DELIBERADA para quien use este hook directamente: `handleDeleteLine` ya no pregunta,
  // BORRA. Quien lo invoque fuera de `SmartMasterDocument` debe confirmar antes.
  const handleDeleteLine = useCallback(
    async (lineId: string) => {
      dispatch({ type: 'SET_SAVING', saving: true });
      try {
        let deleteResult: DeleteLineResult | void = undefined;
        if (state.documentId) {
          // Expone al adapter el rowVersion de la línea Y el de la cabecera (leídos
          // del estado), para los backends que validan concurrencia en el DELETE.
          // Aditivo y opcional. Si el DELETE responde 409, el throw salta a la
          // captura y la línea NO se elimina del estado (DELETE_LINE va después).
          const current = state.lines.find((l) => l.id === lineId);
          const rowVersion = current?.[lineVersionKey];
          const headerRowVersion = state.headerValues[headerVersionKey] ?? state.serverVersion;
          const options: { rowVersion?: string; headerRowVersion?: string } = {};
          if (rowVersion != null) options.rowVersion = String(rowVersion);
          if (headerRowVersion != null) options.headerRowVersion = String(headerRowVersion);
          deleteResult = await adapter.deleteLine(
            state.documentId,
            lineId,
            Object.keys(options).length > 0 ? options : undefined,
          );
        }
        dispatch({ type: 'DELETE_LINE', lineId });

        // Si el backend devuelve headerValues recalculados, aplicarlos directamente.
        // Esto reemplaza la estrategia de autoSaveLineTotals (que el backend ignora).
        const backendHeaderValues = (deleteResult as DeleteLineResult | undefined)?.headerValues;
        if (backendHeaderValues && Object.keys(backendHeaderValues).length > 0) {
          dispatch({
            type: 'MERGE_STATE',
            payload: { headerValues: { ...state.headerValues, ...backendHeaderValues } },
          });
        } else {
          // Sin headerValues del backend → calcular localmente y persistir
          const updatedLines = state.lines.filter((l) => l.id !== lineId);
          await autoSaveLineTotals(updatedLines);
        }

        // Actualizar serverVersion si el backend la retornó
        const newVersion = (deleteResult as DeleteLineResult | undefined)?.serverVersion;
        if (newVersion) {
          dispatch({ type: 'SET_SERVER_VERSION', version: newVersion });
        }
      } catch (err) {
        handleMutationError(err);
      } finally {
        dispatch({ type: 'SET_SAVING', saving: false });
      }
    },
    [state.documentId, state.lines, state.headerValues, state.serverVersion, lineVersionKey, headerVersionKey, adapter, autoSaveLineTotals, handleMutationError],
  );

  // ── Duplicate line ────────────────────────────────
  const handleDuplicateLine = useCallback(
    async (lineId: string) => {
      const original = state.lines.find((l) => l.id === lineId);
      if (!original) return;
      dispatch({ type: 'SET_SAVING', saving: true });
      try {
        // Construir copia sin id/line_number — el backend asignará nuevos valores.
        // Se descarta también el _row_version del original: la copia es una línea
        // NUEVA y no debe heredar el token de versión de su origen. Sí viaja el
        // row_version de la CABECERA para validar concurrencia del header.
        const { id: _id, line_number: _ln, ...rest } = original;
        const baseData = { ...rest };
        delete (baseData as Record<string, unknown>)[lineVersionKey];
        const lineData = withHeaderVersion(baseData, state.headerValues, state.serverVersion, headerVersionKey);
        if (state.documentId && adapter.saveLine) {
          const result = await adapter.saveLine(state.documentId, null, lineData);
          if (result.success) {
            const newLine: DocumentLineData = {
              id: result.lineId,
              line_number: state.lines.length + 1,
              ...lineData,
              ...(result.lineData ?? {}),
            };
            dispatch({ type: 'DUPLICATE_LINE', lineId, newLine });
            await autoSaveLineTotals([...state.lines, newLine]);
            // Sincronizar la versión/cabecera recalculadas por el backend tras el POST
            // /lines. Sin esto la versión local se queda atrás y el poll de conflicto
            // dispara un falso "alguien más editó" (mismo arreglo que en handleLineSave).
            if (result.serverVersion) {
              dispatch({ type: 'SET_SERVER_VERSION', version: result.serverVersion });
            }
            if (result.headerValues) {
              dispatch({
                type: 'MERGE_STATE',
                payload: { headerValues: { ...state.headerValues, ...result.headerValues } },
              });
            }
          }
        } else {
          // Sin backend: duplicar localmente con id temporal
          const newLine: DocumentLineData = {
            ...original,
            id: `local-${Date.now()}`,
            line_number: state.lines.length + 1,
          };
          dispatch({ type: 'DUPLICATE_LINE', lineId, newLine });
        }
      } catch (err) {
        handleMutationError(err);
      } finally {
        dispatch({ type: 'SET_SAVING', saving: false });
      }
    },
    [state.documentId, state.lines, state.headerValues, state.serverVersion, lineVersionKey, headerVersionKey, adapter, autoSaveLineTotals, handleMutationError],
  );

  // ── Transition ────────────────────────────────────
  const handleTransition = useCallback(
    async (to: string) => {
      if (!state.documentId) return;

      // Validate if guard exists
      const transition = (template.stateMachine.transitions ?? []).find(
        (t) => t.to === to && t.from.includes(state.docState),
      );

      // Gating in-flight (Caso B): mientras corre la transición, la barra de
      // acciones queda deshabilitada para reducir la ventana de doble disparo
      // (aprobar vs anular concurrentes).
      dispatch({ type: 'SET_SAVING', saving: true });
      try {
        if (transition?.hasGuard && adapter.validateTransition) {
          const validation = await adapter.validateTransition(state.documentId, to);
          if (!validation.allowed) {
            dispatch({ type: 'SET_ERROR', error: validation.errors?.join(', ') });
            return;
          }
        }

        // execute-process envía el row_version del header; un desfase responde 409
        // (lanzado) y se trata como conflicto duro. La respuesta trae el nuevo
        // serverVersion, que resincronizamos para la próxima mutación.
        const result = await adapter.executeTransition(state.documentId, to, state.docState, state.serverVersion);
        if (result.success) {
          dispatch({ type: 'SET_DOC_STATE', docState: result.newState });
          if (result.serverVersion) {
            dispatch({ type: 'SET_SERVER_VERSION', version: result.serverVersion });
          }
        } else {
          dispatch({ type: 'SET_ERROR', error: result.errors?.join(', ') });
        }
      } catch (err) {
        handleMutationError(err);
      } finally {
        dispatch({ type: 'SET_SAVING', saving: false });
      }
    },
    [state.documentId, state.docState, state.serverVersion, template.stateMachine.transitions, adapter, handleMutationError],
  );

  // ── Execute action ────────────────────────────────
  const handleAction = useCallback(
    async (actionKey: string) => {
      // Gating in-flight (Caso B): deshabilita la barra de acciones mientras la
      // acción (aprobar/anular/guardar) está en curso.
      dispatch({ type: 'SET_SAVING', saving: true });
      try {
        const result = await adapter.executeAction(actionKey, state);
        if (!result.success && result.message) {
          dispatch({ type: 'SET_ERROR', error: result.message });
          return;
        }

        // El documento fue eliminado (delete / cancel que borra): navegar de vuelta
        // al listado en lugar de mezclar estado sobre un documento inexistente.
        if (result.success && result.navigateToList) {
          if (result.message) toast.success(result.message);
          onNavigateToList?.();
          return;
        }

        if (result.updatedData) {
          dispatch({ type: 'MERGE_STATE', payload: result.updatedData });
        }

        // Tras crear el documento, persistir líneas locales (IDs temporales)
        if (actionKey === 'save' && result.success) {
          const newDocId = result.updatedData?.documentId ?? state.documentId;
          if (newDocId) {
            // Segunda fase del guardado. ESTE es el camino que recorre el botón
            // "Guardar" de la barra de acciones: `handleSaveHeader` existe y hace lo
            // propio, pero `SmartMasterDocument` no lo invoca — guarda por acción,
            // vía `adapter.executeAction`. Enganchar solo allá dejaba los campos
            // adicionales sin escribir en la aplicación real aunque las pruebas del
            // reducer pasaran, porque llaman a `handleSaveHeader` directamente.
            await persistenceHooksRef.current?.onHeaderPersisted?.(newDocId);

            const localLines = state.lines.filter((l) => String(l.id).startsWith('local-'));
            for (const line of localLines) {
              try {
                const { id: _id, line_number: _ln, ...lineData } = line;
                const lineResult = await adapter.saveLine(newDocId, null, lineData);
                if (lineResult.success) {
                  dispatch({
                    type: 'UPDATE_LINE',
                    lineId: String(line.id),
                    data: { id: lineResult.lineId, ...(lineResult.lineData ?? {}) },
                  });
                }
              } catch { /* ignorar errores individuales de línea */ }
            }
          }
          onSaved?.();
        }
      } catch (err) {
        handleMutationError(err);
      } finally {
        dispatch({ type: 'SET_SAVING', saving: false });
      }
    },
    [state, adapter, onSaved, onNavigateToList, handleMutationError],
  );

  // ── Approve / Reject ──────────────────────────────
  const handleApprove = useCallback(async () => {
    if (!state.documentId || !adapter.approve) return;
    await adapter.approve(state.documentId);
  }, [state.documentId, adapter]);

  const handleReject = useCallback(async () => {
    if (!state.documentId || !adapter.reject) return;
    await adapter.reject(state.documentId);
  }, [state.documentId, adapter]);

  return {
    state,
    dispatch,
    handleHeaderFieldChange,
    handleSaveHeader,
    handleLineSave,
    handleDeleteLine,
    handleDuplicateLine,
    handleTransition,
    handleAction,
    handleApprove,
    handleReject,
  };
}
